import { useState } from 'react';
import { Box, Checkbox, Group, NumberInput, Select, Stack, Switch, Text, Textarea, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { brand } from '@/theme/theme';

export type JsonValue = string | number | boolean | null | JsonValue[] | { [k: string]: JsonValue };
export type ConfigValue = Record<string, JsonValue>;

interface Prop {
  type?: string | string[];
  enum?: JsonValue[];
  default?: JsonValue;
  description?: string;
  minimum?: number;
  maximum?: number;
}
export interface JsonSchema {
  type?: string;
  properties?: Record<string, Prop>;
  required?: string[];
  additionalProperties?: boolean;
}

/** Defaults from the schema, overlaid by what the caller already has. */
export function withDefaults(schema: JsonSchema, value: ConfigValue): ConfigValue {
  const out: ConfigValue = { ...value };
  for (const [k, p] of Object.entries(schema.properties ?? {})) {
    // a null default is "unset" (forun's odm spells epsg that way), not a value to send
    if (!(k in out) && p.default !== undefined && p.default !== null) out[k] = p.default;
  }
  return out;
}

/** The entries of `edited` that differ from `base`: what an analysis stores as its overrides. */
export function configOverrides(base: ConfigValue, edited: ConfigValue): ConfigValue {
  const out: ConfigValue = {};
  for (const [k, v] of Object.entries(edited)) {
    if (JSON.stringify(v) !== JSON.stringify(base[k])) out[k] = v;
  }
  return out;
}

const SIMPLE = new Set(['string', 'number', 'integer', 'boolean']);
function simpleType(p: Prop): string | null {
  const t = Array.isArray(p.type) ? p.type.find((x) => x !== 'null') : p.type;
  if (p.enum) return 'enum';
  return t && SIMPLE.has(t) ? t : null;
}

/**
 * Mantine hands back the raw text instead of a number for 14+ digits, trailing zeros or a
 * trailing dot, so the text is kept here and parsed; otherwise such a value would be dropped.
 */
function NumberField({
  prop,
  integer,
  value,
  onChange,
  ...common
}: {
  label: string;
  description?: string;
  required: boolean;
  withAsterisk: boolean;
  disabled: boolean;
  prop: Prop;
  integer: boolean;
  value: JsonValue | undefined;
  onChange: (next: number | undefined) => void;
}) {
  const [text, setText] = useState<string | number>(typeof value === 'number' ? value : '');
  const parsed = (n: string | number) => (typeof n === 'number' ? n : n.trim() === '' || !Number.isFinite(Number(n)) ? undefined : Number(n));
  const shown = parsed(text) === (typeof value === 'number' ? value : undefined) ? text : typeof value === 'number' ? value : '';
  return (
    <NumberInput
      {...common}
      value={shown}
      min={prop.minimum}
      max={prop.maximum}
      allowDecimal={!integer}
      onChange={(n) => {
        setText(n);
        onChange(parsed(n));
      }}
    />
  );
}

export interface ConfigFormProps {
  schema: JsonSchema;
  value: ConfigValue;
  onChange: (next: ConfigValue) => void;
  /**
   * What an unset knob falls back to. When given, each field gets an override checkbox:
   * unchecked shows the inherited value disabled and leaves the key out of `value`.
   */
  inherited?: ConfigValue;
}

/** Renders a flat JSON Schema object as typed inputs; anything else falls back to raw JSON. */
export function ConfigForm({ schema, value, onChange, inherited }: ConfigFormProps) {
  const { t } = useTranslation('runs');
  const props = Object.entries(schema.properties ?? {});
  const renderable = props.length > 0 && props.every(([, p]) => simpleType(p) !== null);
  const [raw, setRaw] = useState(() => JSON.stringify(value, null, 2));
  const [rawError, setRawError] = useState<string | null>(null);
  // overridden fields whose value is currently empty, so they stay checked
  const [emptyOverrides, setEmptyOverrides] = useState<Set<string>>(new Set());

  if (!renderable) {
    return (
      <Textarea
        label={t('config.json')}
        description={t('config.jsonHint')}
        value={raw}
        rows={6}
        error={rawError}
        styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
        onChange={(e) => {
          const text = e.currentTarget.value;
          setRaw(text);
          try {
            const parsed = JSON.parse(text) as ConfigValue;
            setRawError(null);
            onChange(parsed);
          } catch {
            setRawError(t('config.invalidJson'));
          }
        }}
      />
    );
  }

  const required = new Set(schema.required ?? []);
  const set = (k: string, v: JsonValue | undefined) => {
    const next = { ...value };
    const empty = v === undefined || v === '';
    if (empty) delete next[k];
    else next[k] = v;
    if (inherited && empty !== emptyOverrides.has(k)) {
      const keys = new Set(emptyOverrides);
      if (empty) keys.add(k);
      else keys.delete(k);
      setEmptyOverrides(keys);
    }
    onChange(next);
  };
  const isOverridden = (k: string) => !inherited || k in value || emptyOverrides.has(k);
  const toggle = (k: string, on: boolean) => {
    const next = { ...value };
    const empty = new Set(emptyOverrides);
    if (on && inherited?.[k] !== undefined) next[k] = inherited[k];
    else if (on) empty.add(k);
    else {
      delete next[k];
      empty.delete(k);
    }
    setEmptyOverrides(empty);
    onChange(next);
  };

  type Common = { label: string; description?: string; required: boolean; withAsterisk: boolean; disabled: boolean };
  const renderField = (k: string, p: Prop, kind: string | null, common: Common, v: JsonValue | undefined) => {
    if (kind === 'enum') {
      return (
        <Select
          key={k}
          {...common}
          data={(p.enum ?? []).map((o) => ({ value: String(o), label: String(o) }))}
          value={v === undefined || v === null ? null : String(v)}
          onChange={(s) => set(k, s === null ? undefined : (p.enum ?? []).find((o) => String(o) === s) ?? s)}
          allowDeselect={!required.has(k)}
        />
      );
    }
    if (kind === 'boolean') {
      return <Switch key={k} label={k} description={common.description} disabled={common.disabled} checked={v === true} onChange={(e) => set(k, e.currentTarget.checked)} />;
    }
    if (kind === 'number' || kind === 'integer') {
      return <NumberField key={k} {...common} prop={p} integer={kind === 'integer'} value={v} onChange={(n) => set(k, n)} />;
    }
    return <TextInput key={k} {...common} value={typeof v === 'string' ? v : ''} onChange={(e) => set(k, e.currentTarget.value)} />;
  };

  return (
    <Stack gap="sm">
      {props.map(([k, p]) => {
        const kind = simpleType(p);
        const overridden = isOverridden(k);
        const common = { label: k, description: p.description, required: required.has(k), withAsterisk: required.has(k), disabled: !overridden };
        const v = overridden ? value[k] : inherited?.[k];
        const field = renderField(k, p, kind, common, v);
        if (!inherited) return field;
        return (
          <Group key={k} align="flex-start" wrap="nowrap" gap="sm">
            <Checkbox mt={kind === 'boolean' ? 2 : 28} aria-label={t('config.override', { name: k })} title={t('config.override', { name: k })} checked={overridden} onChange={(e) => toggle(k, e.currentTarget.checked)} />
            <Box flex={1}>{field}</Box>
          </Group>
        );
      })}
      {props.length === 0 && (
        <Text fz={13} c={brand.textMuted}>
          {t('config.none')}
        </Text>
      )}
    </Stack>
  );
}
